/**
 * Typed wrapper around `import.meta.env`. This is the **only** module that
 * should read `import.meta.env` directly — every other consumer reads from
 * the `env` object exported below.
 *
 * Required vars throw at startup if missing (skipped when `MODE === "test"`
 * so that vitest can run without a populated env).
 *
 * Boolean flags accept the string `"true"` (anything else is `false`).
 */

const raw = import.meta.env as Record<string, string | undefined>;
const isTestMode = import.meta.env.MODE === "test";

function required(key: string): string {
	const value = raw[key];
	if (!value) {
		if (isTestMode) return "";
		throw new Error(`Missing required env var: ${key}`);
	}
	return value;
}

function optional(key: string): string | undefined {
	const value = raw[key];
	return value && value.length > 0 ? value : undefined;
}

function boolFlag(key: string): boolean {
	return raw[key] === "true";
}

const environmentName = required("VITE_ENVIRONMENT_NAME");
const isDevelopment = environmentName === "development";
const isDevSentry = environmentName === "development-sentry";

export const env = {
	// Vite built-ins
	isDev: import.meta.env.DEV,
	mode: import.meta.env.MODE,

	// Build identity
	appVersion: optional("VITE_APP_VERSION") ?? "unknown",
	buildDate: optional("VITE_BUILD_DATE") ?? "unknown",
	environmentName,
	isProduction: environmentName === "production",
	isDevelopment,
	isDevSentry,

	// Backend
	rogoApiUrl: required("VITE_ROGO_API_URL"),
	fileServer: optional("VITE_FILE_SERVER"),

	// Sentry
	sentryDsn: optional("VITE_SENTRY_DSN"),
	enableSentry: boolFlag("VITE_ENABLE_SENTRY"),

	// Offline sandbox (ROGO_OFFLINE backend): enables sandbox-only conveniences
	// such as auto-loading the Jobs dashboard without a manual view selection.
	offline: boolFlag("VITE_ROGO_OFFLINE"),

	// Dev / debug toggles
	debug: isDevelopment || isDevSentry || boolFlag("VITE_DEBUG"),
	reactScan: boolFlag("VITE_REACT_SCAN"),
	perfMonitor: boolFlag("VITE_PERF_MONITOR"),
	// Default-on flag: only `"false"` disables (matches the legacy semantics).
	enableMrtVirtualization: raw["VITE_ENABLE_MRT_VIRTUALIZATION"] !== "false",

	// Agrian / AgX OAuth
	agxClientId: optional("VITE_AGX_CLIENT_ID"),
	agxAuthBaseUrl: optional("VITE_AGX_AUTH_BASE_URL"),
	agxAuthEndpoint: optional("VITE_AGX_AUTH_ENDPOINT"),
	agxRogoRedirectUri: optional("VITE_AGX_ROGO_REDIRECT_URI"),
} as const;
